import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as SIP from "sip.js";
import { useMsal } from "@azure/msal-react";
import { getApiErrorMessage } from "./api/phoneApi";
import { PhoneContext } from "./PhoneContext";
import { useBrowserCallingSupport } from "./hooks/useBrowserCallingSupport";
import { usePhoneCallLogger } from "./hooks/usePhoneCallLogger";
import { usePhoneConfig } from "./hooks/usePhoneConfig";
import { normalizePhoneNumber, isAllowedPhoneNumber } from "./phoneNumberUtils";

const STATUS = {
  INITIALIZING: "initializing",
  REGISTERING: "registering",
  READY: "ready",
  CALLING: "calling",
  RINGING: "ringing",
  ON_CALL: "on-call",
  ENDING: "ending",
  DISCONNECTED: "disconnected",
  DISABLED: "disabled",
  ERROR: "error",
};

const isRegistererRegistered = (registerer) => {
  return registerer?.state === SIP.RegistererState?.Registered;
};

const isPhoneConfigForbidden = (error) => {
  return error?.response?.status === 403 || error?.statusCode === 403;
};

export const PhoneProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = instance.getActiveAccount() || accounts[0] || null;
  const accountKey = account?.homeAccountId || account?.username || null;
  const browserCallingSupport = useBrowserCallingSupport();
  const { getAccessToken, loadPhoneConfig } = usePhoneConfig({
    instance,
    account,
  });
  const { logCallEvent } = usePhoneCallLogger({ getAccessToken });

  const [status, setStatus] = useState(STATUS.DISCONNECTED);
  const [error, setError] = useState(null);
  const [phoneConfig, setPhoneConfig] = useState(null);
  const [activeDestination, setActiveDestination] = useState(null);

  const userAgentRef = useRef(null);
  const registererRef = useRef(null);
  const sessionRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const initializedRef = useRef(false);
  const phoneDisabledRef = useRef(false);
  const initializingPromiseRef = useRef(null);
  const mountedRef = useRef(true);

  const cleanupCall = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
    }

    sessionRef.current = null;
    setActiveDestination(null);
  }, []);

  const stopPhone = useCallback(
    async ({ unregister = true } = {}) => {
      const activeSession = sessionRef.current;
      const registerer = registererRef.current;
      const userAgent = userAgentRef.current;

      cleanupCall();
      registererRef.current = null;
      userAgentRef.current = null;
      initializedRef.current = false;
      initializingPromiseRef.current = null;

      try {
        if (activeSession?.state === SIP.SessionState.Established) {
          await activeSession.bye().catch(() => null);
        } else if (
          activeSession &&
          activeSession.state !== SIP.SessionState.Terminated
        ) {
          await activeSession.dispose?.().catch?.(() => null);
        }

        if (unregister && isRegistererRegistered(registerer)) {
          await registerer.unregister().catch(() => null);
        }

        if (userAgent) {
          await userAgent.stop().catch(() => null);
        }
      } catch (teardownError) {
        console.warn("Phone teardown error", teardownError);
      }
    },
    [cleanupCall]
  );

  const attachRemoteAudio = useCallback((activeSession) => {
    const sdh = activeSession.sessionDescriptionHandler;

    if (!sdh || !sdh.peerConnection) {
      console.warn("No peer connection available for remote audio.");
      return;
    }

    const remoteStream = new MediaStream();

    sdh.peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === "audio") {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (!remoteAudioRef.current) {
      remoteAudioRef.current = new Audio();
      remoteAudioRef.current.autoplay = true;
    }

    remoteAudioRef.current.srcObject = remoteStream;
    remoteAudioRef.current.play().catch((audioError) => {
      console.warn("Browser blocked call audio playback", audioError);
      setError("Call connected, but the browser blocked audio playback. Click the page and try again.");
    });
  }, []);

  const initializePhone = useCallback(async () => {
    if (phoneDisabledRef.current) {
      setStatus(STATUS.DISABLED);
      return null;
    }

    if (!browserCallingSupport.supported) {
      setStatus(STATUS.ERROR);
      setError(browserCallingSupport.reason);
      return null;
    }

    if (!account || inProgress !== "none") {
      setStatus(STATUS.DISCONNECTED);
      setError("Waiting for Microsoft login to finish before connecting phone.");
      return null;
    }

    if (initializedRef.current && userAgentRef.current && registererRef.current) {
      return { userAgent: userAgentRef.current, registerer: registererRef.current };
    }

    if (initializingPromiseRef.current) {
      return initializingPromiseRef.current;
    }

    setStatus(STATUS.INITIALIZING);
    setError(null);

    const initPromise = (async () => {
      let userAgent = null;
      let registerer = null;

      try {
        const config = await loadPhoneConfig();
        setPhoneConfig(config);

        const uri = SIP.UserAgent.makeURI(
          `sip:${config.extension}@${config.sipDomain}`
        );

        if (!uri) {
          throw new Error("Invalid SIP URI returned by phone config.");
        }

        userAgent = new SIP.UserAgent({
          uri,
          authorizationUsername: config.extension,
          authorizationPassword: config.password,
          transportOptions: {
            server: config.wssServer,
            traceSip: false,
          },
          logLevel: "warn",
          sessionDescriptionHandlerFactoryOptions: {
            peerConnectionConfiguration: {
              iceServers: [
                {
                  urls: "stun:stun.l.google.com:19302",
                },
              ],
            },
          },
        });

        userAgent.transport.stateChange.addListener((transportState) => {
          if (!mountedRef.current) {
            return;
          }

          console.log("PHONE TRANSPORT STATE", transportState);

          if (transportState === SIP.TransportState.Connected) {
            setStatus(STATUS.REGISTERING);
            setError(null);
          }

          if (transportState === SIP.TransportState.Disconnected) {
            setStatus((currentStatus) =>
              currentStatus === STATUS.READY ? STATUS.DISCONNECTED : currentStatus
            );
            setError("Phone server disconnected. Confirm WSS is reachable and the SIP extension can register.");
          }
        });

        userAgent.delegate = {
          onDisconnect: (disconnectError) => {
            console.warn("Phone disconnected", disconnectError);
            setStatus(STATUS.DISCONNECTED);
            setError("Phone disconnected from the server.");
          },
          onInvite: (invitation) => {
            console.log("Inbound call rejected by outbound-only dialer.");
            invitation.reject();
          },
        };

        registerer = new SIP.Registerer(userAgent, {
          expires: 300,
        });

        registerer.stateChange.addListener((registererState) => {
          console.log("PHONE REGISTERER STATE", registererState);
        });

        userAgentRef.current = userAgent;
        registererRef.current = registerer;

        await userAgent.start();

        if (!mountedRef.current) {
          await userAgent.stop().catch(() => null);
          return null;
        }

        await registerer.register();

        if (!mountedRef.current) {
          await stopPhone({ unregister: true });
          return null;
        }

        initializedRef.current = true;
        setStatus(STATUS.READY);
        setError(null);

        return { userAgent, registerer };
      } catch (initError) {
        if (isPhoneConfigForbidden(initError)) {
          console.info("Phone calling is disabled for this account.");
          phoneDisabledRef.current = true;
          initializedRef.current = false;
          setPhoneConfig(null);
          setError(null);
          setStatus(STATUS.DISABLED);
          await userAgent?.stop?.().catch?.(() => null);
          return null;
        }

        console.error("Phone initialization failed", initError);

        if (registererRef.current === registerer) {
          registererRef.current = null;
        }

        if (userAgentRef.current === userAgent) {
          userAgentRef.current = null;
        }

        await userAgent?.stop?.().catch?.(() => null);

        initializedRef.current = false;
        setError(
          getApiErrorMessage(
            initError,
            "Phone initialization failed. Confirm your account has a phone extension assigned."
          )
        );
        setStatus(STATUS.ERROR);
        return null;
      } finally {
        initializingPromiseRef.current = null;
      }
    })();

    initializingPromiseRef.current = initPromise;
    return initPromise;
  }, [account, browserCallingSupport, inProgress, loadPhoneConfig, stopPhone]);

  useEffect(() => {
    mountedRef.current = true;

    if (accountKey && inProgress === "none" && !phoneDisabledRef.current) {
      initializePhone();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [accountKey, inProgress, initializePhone]);

  useEffect(() => {
    phoneDisabledRef.current = false;
    setPhoneConfig(null);
    setError(null);
    setStatus(accountKey ? STATUS.DISCONNECTED : STATUS.DISABLED);
  }, [accountKey]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopPhone({ unregister: true });
    };
  }, [stopPhone]);

  const call = useCallback(
    async (rawDestination, metadata = {}) => {
      const destination = normalizePhoneNumber(rawDestination);
      setError(null);

      if (phoneDisabledRef.current || status === STATUS.DISABLED) {
        throw new Error("Phone calling is not enabled for this account.");
      }

      if (!browserCallingSupport.supported) {
        throw new Error(browserCallingSupport.reason || "Browser calling is not supported.");
      }

      if (!isAllowedPhoneNumber(destination)) {
        throw new Error("Enter a valid phone number.");
      }

      if (!userAgentRef.current || !phoneConfig || status !== STATUS.READY) {
        await initializePhone();
      }

      if (!userAgentRef.current || !phoneConfig || status === STATUS.ERROR) {
        throw new Error(error || "Phone is not ready yet.");
      }

      if (
        sessionRef.current &&
        sessionRef.current.state !== SIP.SessionState.Terminated
      ) {
        throw new Error("A call is already active.");
      }

      const targetUri = SIP.UserAgent.makeURI(
        `sip:${destination}@${phoneConfig.sipDomain}`
      );

      if (!targetUri) {
        throw new Error("Invalid call destination.");
      }

      await logCallEvent(
        "call_started",
        {
          destination,
          ...metadata,
        },
        { required: true }
      );

      const inviter = new SIP.Inviter(userAgentRef.current, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false,
          },
        },
      });

      sessionRef.current = inviter;
      setActiveDestination(destination);
      setStatus(STATUS.CALLING);

      inviter.stateChange.addListener((sessionState) => {
        console.log("PHONE SESSION STATE", sessionState);

        switch (sessionState) {
          case SIP.SessionState.Establishing:
            setStatus(STATUS.RINGING);
            break;
          case SIP.SessionState.Established:
            setStatus(STATUS.ON_CALL);
            attachRemoteAudio(inviter);
            logCallEvent("call_established", {
              destination,
              ...metadata,
            });
            break;
          case SIP.SessionState.Terminating:
            setStatus(STATUS.ENDING);
            break;
          case SIP.SessionState.Terminated:
            cleanupCall();
            setStatus(STATUS.READY);
            logCallEvent("call_ended", {
              destination,
              ...metadata,
            });
            break;
          default:
            break;
        }
      });

      try {
        await inviter.invite();
      } catch (callError) {
        cleanupCall();
        setStatus(STATUS.READY);
        const message = callError.message || "Call failed.";
        setError(message);
        await logCallEvent("call_failed", {
          destination,
          error: message,
          ...metadata,
        });
        throw callError;
      }
    },
    [
      attachRemoteAudio,
      browserCallingSupport,
      cleanupCall,
      error,
      initializePhone,
      logCallEvent,
      phoneConfig,
      status,
    ]
  );

  const hangUp = useCallback(async () => {
    const activeSession = sessionRef.current;

    if (!activeSession) {
      return;
    }

    try {
      switch (activeSession.state) {
        case SIP.SessionState.Initial:
        case SIP.SessionState.Establishing:
          if (activeSession instanceof SIP.Inviter) {
            await activeSession.cancel();
          } else {
            await activeSession.reject();
          }
          break;
        case SIP.SessionState.Established:
          await activeSession.bye();
          break;
        default:
          break;
      }
    } catch (hangupError) {
      console.error("Hangup failed", hangupError);
      setError(hangupError.message || "Hangup failed.");
      throw hangupError;
    }
  }, []);

  const value = useMemo(
    () => ({
      status,
      error,
      phoneConfig,
      activeDestination,
      browserCallingSupport,
      isReady: status === STATUS.READY,
      isDisabled: status === STATUS.DISABLED,
      isCalling: [STATUS.CALLING, STATUS.RINGING, STATUS.ON_CALL].includes(
        status
      ),
      call,
      hangUp,
      connect: initializePhone,
    }),
    [
      activeDestination,
      browserCallingSupport,
      call,
      error,
      hangUp,
      initializePhone,
      phoneConfig,
      status,
    ]
  );

  return (
    <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>
  );
};
